// UA-only detection for browsers whose defaults block third-party
// payment endpoints (specifically api.intuit.com). Returns a stable
// `id` for telemetry keys and a `name` for display copy. No support
// copy lives here on purpose: the customer-facing fix instructions
// live in the component so copy changes don't feel like util changes.
//
// Runtime signal (navigator.brave) is consulted separately by the
// caller because Brave sometimes omits the "Brave" UA token on iOS
// and desktop; we still want to name it when that runtime flag exists.
export function detectBlockingBrowserFromUA(userAgent) {
  const ua = String(userAgent || '');
  // DDG on iOS uses either the short "Ddg/" or the long "DuckDuckGo/"
  // depending on OS/app version; match both so the shield instruction
  // reaches the customer instead of the generic fallback.
  if (/\b(?:Ddg|DuckDuckGo)\//.test(ua)) return { id: 'ddg', name: 'DuckDuckGo' };
  if (/\bBrave\b/i.test(ua))             return { id: 'brave', name: 'Brave' };
  // Focus must be checked before FxiOS because Firefox Focus for iOS
  // ships a UA that contains both "FxiOS" and "Focus"; the Focus
  // recovery path (open in Safari/Chrome) is different from Firefox
  // for iOS (toggle ETP), so misclassifying loses the right hint.
  if (/\bFocus\b/i.test(ua))             return { id: 'firefox-focus', name: 'Firefox Focus' };
  if (/FxiOS/.test(ua))                  return { id: 'firefox-ios',   name: 'Firefox' };
  return { id: 'generic', name: null };
}

export function detectBlockingBrowser() {
  if (typeof navigator === 'undefined') return { id: 'generic', name: null };
  const fromUA = detectBlockingBrowserFromUA(navigator.userAgent || '');
  if (fromUA.id !== 'generic') return fromUA;
  // Brave on iOS and some desktop configs strips "Brave" from the UA
  // but exposes navigator.brave.isBrave(). Best-effort; sync path first.
  try {
    if (navigator.brave && typeof navigator.brave.isBrave === 'function') {
      return { id: 'brave-runtime', name: 'Brave' };
    }
  } catch { /* navigator.brave may throw in strict CSP contexts */ }
  return fromUA;
}
